(() => {
  let apiReady = false;
  let gtmLoaded = false;
  let gtmScheduled = false;
  let clarityLoaded = false;
  let restarting = false;
  const activeResources = new Map();
  let previous = null;
  let attempts = 0;

  function permissions() {
    const api = apiReady && window.Shopify?.customerPrivacy;
    const allowed = (method) => {
      try { return api && typeof api[method] === 'function' && api[method]() === true; }
      catch (_error) { return false; }
    };
    return {
      analytics: !!allowed('analyticsProcessingAllowed'),
      marketing: !!allowed('marketingAllowed'),
      preferences: !!allowed('preferencesProcessingAllowed')
    };
  }

  // Include resources started outside this controller and SDKs still initializing.
  function registerResource(name, purposes) {
    const consent = permissions();
    if (restarting || !purposes.every((purpose) => consent[purpose] === true)) return false;
    const used = activeResources.get(name) || new Set();
    purposes.forEach((purpose) => used.add(purpose));
    activeResources.set(name, used);
    return true;
  }
  const grantedPurposes = (consent) => Object.keys(consent).filter((purpose) => consent[purpose]);

  const preferenceKeys = ['theme:popup-filled', 'theme:popup-appeared', 'theme:recently-viewed-products'];
  function clearPreferences() {
    try {
      [...preferenceKeys, 'mp:pro-popup'].forEach((key) => localStorage.removeItem(key));
      ['mp:pro-popup:vistes', 'mp:pro-popup:vist'].forEach((key) => sessionStorage.removeItem(key));
    } catch (_error) { /* Storage can be unavailable in private browsing. */ }
  }

  window.mpPrivacy = {
    analyticsAllowed: () => permissions().analytics,
    preferencesAllowed: () => permissions().preferences,
    marketingAllowed: () => permissions().marketing,
    mediaAllowed: () => { const consent = permissions(); return !restarting && consent.analytics && consent.marketing; },
    registerResource,
    getPreference(key) {
      if (!permissions().preferences || !preferenceKeys.includes(key)) return null;
      try {
        const stored = JSON.parse(localStorage.getItem(key));
        if (!stored || typeof stored.expires !== 'number' || stored.expires <= Date.now()) {
          localStorage.removeItem(key);
          return null;
        }
        return stored.value;
      } catch (_error) { return null; }
    },
    setPreference(key, value) {
      if (!permissions().preferences || !preferenceKeys.includes(key)) return;
      const days = key === 'theme:recently-viewed-products' ? 30 : 7;
      try { localStorage.setItem(key, JSON.stringify({ value, expires: Date.now() + days * 86400000 })); }
      catch (_error) { /* Storage can be unavailable in private browsing. */ }
    }
  };

  function refreshPreferencesLink() {
    const available = typeof window.privacyBanner?.showPreferences === 'function';
    const menuItems = [...document.querySelectorAll('[data-mp-cookie-prefs-item]')];
    menuItems.forEach((item) => { item.hidden = !available; });
    document.querySelectorAll('[data-cookie-preferences]').forEach((link) => { link.hidden = !available || menuItems.length > 0; });
  }

  // Preserve the published robot filter; storage only remembers visit exclusion flags.
  const botParams = ['color', 'color_puerta', 'cat', 'grosor', 'madera', 'biselado',
    'compatible_con_sistemas_de_cal', 'price', 'resistencia_al_agua', 'resistencia_dureza',
    'manufacturer', 'p', 'cb', '_fd', '_pp', '_mpqa'];
  function isBotLanding() {
    let flagged = false;
    try { flagged = sessionStorage.getItem('mp_bot_landing') === '1'; } catch (_error) {}
    const keys = [...new URLSearchParams(window.location.search).keys()].map((key) => key.toLowerCase());
    const bot = document.getElementById('mp-clarity-data')?.dataset.qaCart === 'true' || flagged || keys.some((key) => botParams.includes(key)) ||
      /\.shopifypreview\.com$/.test(window.location.hostname) || window.navigator?.webdriver === true;
    if (bot) { try { sessionStorage.setItem('mp_bot_landing', '1'); } catch (_error) {} }
    return bot;
  }
  window.mpPrivacy.isBotLanding = isBotLanding;
  // Remember QA landings even when consent is denied and the visitor navigates away.
  isBotLanding();
  let qaCartPending = false;
  async function markQaCart() {
    const data = document.getElementById('mp-clarity-data')?.dataset;
    let qa = new URLSearchParams(window.location.search.toLowerCase()).has('_mpqa');
    try { qa = qa || sessionStorage.getItem('mp_qa_landing') === '1'; } catch (_error) {}
    if (!qa) return;
    try { sessionStorage.setItem('mp_qa_landing', '1'); } catch (_error) {}
    // Marca interna de proves (sense dades personals): no depèn del consentiment.
    if (qaCartPending || data?.qaCart === 'true' || !data?.cartUpdateUrl) return;
    qaCartPending = true;
    try {
      const response = await fetch(data.cartUpdateUrl + '.js', {
        method: 'POST', credentials: 'same-origin', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _mpqa: '1' } })
      });
      if (response.ok) data.qaCart = 'true';
    } catch (_error) { /* Retry on the next consent sync if the cart could not be marked. */ }
    finally { qaCartPending = false; }
  }
  markQaCart();

  function consentEvent(consent) {
    window.dataLayer.push({ event: 'mp_consent_update', mp_analytics_allowed: consent.analytics,
      mp_marketing_allowed: consent.marketing, mp_preferences_allowed: consent.preferences });
  }

  function loadOptionalScripts() {
    const consent = permissions();
    markQaCart();
    if (!(consent.analytics || consent.marketing) || isBotLanding()) return;
    if (!gtmLoaded && !gtmScheduled) {
      gtmScheduled = true;
      const inject = () => {
        const current = permissions();
        gtmScheduled = false;
        if (gtmLoaded || !(current.analytics || current.marketing) || isBotLanding()) return;
        if (!registerResource('gtm', grantedPurposes(current))) return;
        gtmLoaded = true;
        window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-WW75VWH';
        document.head.appendChild(script);
        consentEvent(current);
      };
      const whenIdle = () => {
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(inject, { timeout: 3000 });
        else setTimeout(inject, 1500);
      };
      if (document.readyState === 'complete') whenIdle();
      else window.addEventListener('load', whenIdle, { once: true });
    }
    if (consent.analytics && !clarityLoaded) {
      const injectClarity = () => {
        if (clarityLoaded || !permissions().analytics || isBotLanding()) return;
        const current = permissions();
        if (!registerResource('clarity', ['analytics', ...(current.marketing ? ['marketing'] : [])])) return;
        clarityLoaded = true;
        window.clarity = window.clarity || function () {
          (window.clarity.q = window.clarity.q || []).push(arguments);
        };
        updateClarityConsent(permissions());
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.clarity.ms/tag/qigvrq44ab';
        document.head.appendChild(script);
        document.dispatchEvent(new CustomEvent('mp:clarity-ready'));
      };
      if (document.readyState === 'complete') injectClarity();
      else window.addEventListener('load', injectClarity, { once: true });
    }
  }

  function updateClarityConsent(consent) {
    if (!clarityLoaded) return;
    registerResource('clarity', ['analytics', ...(consent.marketing ? ['marketing'] : [])]);
    window.clarity('consentv2', {
      ad_Storage: consent.marketing ? 'granted' : 'denied',
      analytics_Storage: consent.analytics ? 'granted' : 'denied'
    });
  }

  function syncConsent() {
    refreshPreferencesLink();
    if (!apiReady || restarting) return;
    const consent = permissions();
    if (previous && Object.keys(consent).every((key) => consent[key] === previous[key])) return;
    window.gtag('consent', 'update', {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied',
      functionality_storage: consent.preferences ? 'granted' : 'denied',
      personalization_storage: consent.preferences ? 'granted' : 'denied'
    });
    updateClarityConsent(consent);
    const revoked = previous && [...activeResources.values()].some((purposes) =>
      [...purposes].some((purpose) => previous[purpose] && !consent[purpose]));
    previous = consent;
    if (revoked) restarting = true;
    if (!consent.preferences) clearPreferences();
    document.dispatchEvent(new CustomEvent('mp:privacy-changed', { detail: consent }));
    // Already executed third-party code cannot be unloaded: restart after recording withdrawal.
    if (revoked) {
      Promise.resolve(window.mpAttributionCleanup).finally(() => window.location.reload());
      return;
    }
    loadOptionalScripts();
    if (gtmLoaded) {
      registerResource('gtm', grantedPurposes(consent));
      consentEvent(consent);
    }
  }

  document.addEventListener('visitorConsentCollected', () => {
    if (window.Shopify?.customerPrivacy) apiReady = true;
    syncConsent();
  });
  document.addEventListener('shopify:section:load', refreshPreferencesLink);
  window.addEventListener('focus', syncConsent);
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-cookie-preferences], [data-mp-cookie-prefs]');
    if (!link) return;
    event.preventDefault();
    window.privacyBanner?.showPreferences?.();
  });

  function loadPrivacyApi() {
    if (!window.Shopify?.loadFeatures) {
      if (++attempts <= 60) setTimeout(loadPrivacyApi, 500);
      return;
    }
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error && window.Shopify.customerPrivacy) { apiReady = true; syncConsent(); }
    });
  }
  loadPrivacyApi();
  let polls = 0;
  const bannerPoll = setInterval(() => {
    refreshPreferencesLink();
    if (typeof window.privacyBanner?.showPreferences === 'function' || ++polls >= 60) clearInterval(bannerPoll);
  }, 500);
})();
